import Button from '@material-ui/core/Button';
import CardActions from '@material-ui/core/CardActions';
import Grid from '@material-ui/core/Grid';
import ChangeAllPlacementCardAngle from 'Components/Editor/Details/ChangeAllPlacements/ChangeAllPlacementCardAngle';
import ChangeAllPlacementCardArea from 'Components/Editor/Details/ChangeAllPlacements/ChangeAllPlacementCardArea';
import ChangeAllPlacementCardBrands from 'Components/Editor/Details/ChangeAllPlacements/ChangeAllPlacementCardBrands';
import ChangeAllPlacementCardPlacements from 'Components/Editor/Details/ChangeAllPlacements/ChangeAllPlacementCardPlacements';
import ChangeAllPlacementCardPosition from 'Components/Editor/Details/ChangeAllPlacements/ChangeAllPlacementCardPosition';
import ChangeAllPlacementCardTime from 'Components/Editor/Details/ChangeAllPlacements/ChangeAllPlacementCardTime';
import ChangeAllPlacementCardPerspective from 'Components/Editor/Details/ChangeAllPlacements/ChangeAllPlacementCardPerspective';
import ChangeAllPlacementModal from 'Components/Editor/Details/ChangeAllPlacements/ChangeAllPlacementModal';
import { UseVideoPlayerInstance } from 'Hooks/Editor/useVideoPlayerInstance';
import Brand from 'Models/Brands/Brand';
import {
	DetectionAngleSpecification,
	DetectionAreaSpecification,
	DetectionPositionSpecification,
	DetectionTimeSpecification,
	DetectionPerspectiveSpecification,
} from 'Models/Project/ProjectFilterSpecifications';
import { ReactElement, useMemo, useState } from 'react';
import { PutChangeAllPlacementsRequest } from 'Utils/Http/Requests/Videos/Detections/PutChangeAllPlacementsRequest';
import {
	getChangeAllPlacementAngle,
	getChangeAllPlacementArea,
	getChangeAllPlacementBrands,
	getChangeAllPlacementLogoIds,
	getChangeAllPlacementPlacementIds,
	getChangeAllPlacementTextIds,
	getChangeAllPlacementTime,
	getChangeAllPlacementPerspective,
	setChangeAllPlacementAngle,
	setChangeAllPlacementArea,
	setChangeAllPlacementBrands,
	setChangeAllPlacementLogoIds,
	setChangeAllPlacementPlacementIds,
	setChangeAllPlacementPosition,
	setChangeAllPlacementTextIds,
	setChangeAllPlacementTime,
	setChangeAllPlacementPerspective,
} from 'Utils/LocalStorageHelper';

interface ChangeAllPlacementsDetailsProps {
	instance: UseVideoPlayerInstance;
}

const ChangeAllPlacementsDetails = (props: ChangeAllPlacementsDetailsProps): ReactElement => {
	const [selectedPlacementIds, setSelectedPlacementIds] = useState<number[] | null>(
		getChangeAllPlacementPlacementIds()
	);
	const [selectedBrands, setSelectedBrands] = useState<Brand[] | null>(getChangeAllPlacementBrands());
	const [selectedTexts, setSelectedTexts] = useState<number[] | null>(getChangeAllPlacementTextIds());
	const [selectedLogos, setSelectedLogos] = useState<number[] | null>(getChangeAllPlacementLogoIds());
	const [angle, setAngle] = useState<DetectionAngleSpecification | null>(getChangeAllPlacementAngle());
	const [area, setArea] = useState<DetectionAreaSpecification | null>(getChangeAllPlacementArea());
	const [time, setTime] = useState<DetectionTimeSpecification | null>(getChangeAllPlacementTime());
	const [perspective, setPerspective] = useState<DetectionPerspectiveSpecification | null>(
		getChangeAllPlacementPerspective()
	);
	const [isChangeModalOpen, setIsChangeModalOpen] = useState<boolean>(false);

	const request: Omit<PutChangeAllPlacementsRequest, 'placementId'> = {
		placement: selectedPlacementIds !== null ? { placementIds: selectedPlacementIds } : null,
		brand: selectedBrands !== null ? { brandIds: selectedBrands.map((x) => x.id) } : null,
		logoOrText:
			selectedTexts !== null || selectedLogos !== null
				? {
						textIds: selectedTexts ?? [],
						logoIds: selectedLogos ?? [],
				  }
				: null,
		angle: angle,
		area: area,
		time: time,
		position: props.instance.exampleDetectionPosition,
		perspective: perspective,
	};

	const selectedDetections = useMemo(
		() => props.instance.detections.filter((x) => props.instance.selectedDetectionIds.includes(x.id)),
		[props.instance.detections, props.instance.selectedDetectionIds]
	);

	const handleChangeAllPlacementSelectedIdsChanged = (ids: number[] | null): void => {
		setSelectedPlacementIds(ids);
		setChangeAllPlacementPlacementIds(ids);
	};

	const handleChangeAllPlacementBrandsChanged = (brands: Brand[] | null): void => {
		setSelectedBrands(brands);
		setChangeAllPlacementBrands(brands);
	};

	const handleChangeAllPlacementTextsChanged = (texts: number[] | null): void => {
		setSelectedTexts(texts);
		setChangeAllPlacementTextIds(texts);
	};

	const handleChangeAllLogos = (ids: number[] | null): void => {
		setSelectedLogos(ids);
		setChangeAllPlacementLogoIds(ids);
	};

	const handleChangeAllAngleChanged = (angle: DetectionAngleSpecification | null): void => {
		setAngle(angle);
		setChangeAllPlacementAngle(angle);
	};

	const handleChangeAllPlacementAreaChanged = (area: DetectionAreaSpecification | null): void => {
		setArea(area);
		setChangeAllPlacementArea(area);
	};

	const handleChangeAllPlacementPositionChanged = (position: DetectionPositionSpecification | null): void => {
		props.instance.setExampleDetectionPosition(position);
		setChangeAllPlacementPosition(position);
	};

	const handleChangeAllPlacementTimeChange = (time: DetectionTimeSpecification | null): void => {
		setTime(time);
		setChangeAllPlacementTime(time);
	};

	const handleChangeAllPlacementPerspectiveChanged = (perspective: DetectionPerspectiveSpecification | null): void => {
		setPerspective(perspective);
		setChangeAllPlacementPerspective(perspective);
	};

	const handleOpenModalButtonClicked = (): void => {
		setIsChangeModalOpen((prev) => !prev);
	};

	const handleChangeAllPlacementModalClose = (): void => {
		setIsChangeModalOpen(false);
	};

	const handleDetectionsChanged = (): void => {
		setIsChangeModalOpen(false);
		props.instance.clearDetections();
	};

	return (
		<>
			<Grid container={true} spacing={2}>
				<Grid item={true} xs={12} md={6} lg={4}>
					<ChangeAllPlacementCardPlacements
						placements={props.instance.placements}
						selectedIds={selectedPlacementIds}
						onSelectedIdsChanged={handleChangeAllPlacementSelectedIdsChanged}
						selectedDetections={selectedDetections}
					/>
				</Grid>
				<Grid item={true} xs={12} md={6} lg={4}>
					<ChangeAllPlacementCardBrands
						brands={props.instance.brands}
						selectedBrands={selectedBrands}
						onSelectedBrandsChanged={handleChangeAllPlacementBrandsChanged}
						selectedTexts={selectedTexts}
						onSelectedTextsChanged={handleChangeAllPlacementTextsChanged}
						selectedLogos={selectedLogos}
						onSelectedLogosChanged={handleChangeAllLogos}
						selectedDetections={selectedDetections}
					/>
				</Grid>
				<Grid item={true} xs={12} md={6} lg={4}>
					<ChangeAllPlacementCardAngle
						angle={angle}
						onAngleChanged={handleChangeAllAngleChanged}
						selectedDetections={selectedDetections}
					/>
				</Grid>
				<Grid item={true} xs={12} md={6} lg={4}>
					<ChangeAllPlacementCardArea
						area={area}
						onAreaChanged={handleChangeAllPlacementAreaChanged}
						selectedDetections={selectedDetections}
					/>
				</Grid>
				<Grid item={true} xs={12} md={6} lg={4}>
					<ChangeAllPlacementCardPosition
						position={props.instance.exampleDetectionPosition}
						onPositionChange={handleChangeAllPlacementPositionChanged}
						instance={props.instance}
						selectedDetections={selectedDetections}
					/>
				</Grid>
				<Grid item={true} xs={12} md={6} lg={4}>
					<ChangeAllPlacementCardTime
						time={time}
						onTimeChanged={handleChangeAllPlacementTimeChange}
						videoDuration={props.instance.videoNode?.duration ?? 0}
					/>
				</Grid>
				<Grid item={true} xs={12} md={6} lg={4}>
					<ChangeAllPlacementCardPerspective
						perspective={perspective}
						onPerspectiveChanged={handleChangeAllPlacementPerspectiveChanged}
						selectedDetections={selectedDetections}
					/>
				</Grid>
			</Grid>
			<br />
			<CardActions>
				<Button variant="contained" onClick={handleOpenModalButtonClicked}>
					Open change all details modal
				</Button>
			</CardActions>
			<ChangeAllPlacementModal
				open={isChangeModalOpen}
				request={request}
				placements={props.instance.placements}
				videoId={props.instance.video?.id ?? 0}
				onClose={handleChangeAllPlacementModalClose}
				onDetectionChanged={handleDetectionsChanged}
			/>
		</>
	);
};

export default ChangeAllPlacementsDetails;
